var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var _a, _b;
import { action, computed, observable, toJS, when, makeObservable } from 'mobx';
import _get from 'lodash/get';
import { DEFAULT_EMPTY_VALUE, DEFAULT_USER_PERMISSIONS, DEFAULT_USER_PROFILE, GENDERS, TITLES } from '../framework/constants/profile';
import ProfileAPI from '../services/ProfileAPI';
import OdooSyncAPI from '../services/OdooSyncAPI';
import { IDealerProfile, IDealerProfileEditType, DealerStatus, transform } from '../types/DealerProfileTypes';
import UserActionsContactAPI from '../services/UserActionsContactAPI';
import { ShowNotificationError } from 'framework/ErrorLogger';
import { LOCAL_STORAGE_KEY } from '../framework/constants/app';
class ProfileStore {
    constructor() {
        // user object from our php profile service
        Object.defineProperty(this, "userProfile", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: DEFAULT_USER_PROFILE
        });
        Object.defineProperty(this, "userPermissions", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: DEFAULT_USER_PERMISSIONS
        });
        Object.defineProperty(this, "permissionsSubmitting", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "dealersLoading", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "profileLoading", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "errors", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "isFormUpdated", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "dealers", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "isProfileFilled", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "areTermsFilled", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: true
        });
        Object.defineProperty(this, "totalOrdered", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "orderedCars", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        }); // TODO: create interface for orderedCars, when the object is formatted by the backend
        Object.defineProperty(this, "requestedCars", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "wonCars", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "isFetching", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "isSaving", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "hasError", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "selectedDeiveryAddressValue", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: ''
        });
        Object.defineProperty(this, "selectedDeliveryCheckoutAddressObj", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: { street: '', number: '', zip: '', city: '' }
        });
        Object.defineProperty(this, "apiPartners", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "platforms", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "dealerProfile", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {}
        });
        Object.defineProperty(this, "dealerProfileEdit", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {}
        });
        Object.defineProperty(this, "tempPassword", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "clearDealers", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.dealers = [];
            }
        });
        /**
         * Loads full user profile data including
         * user permissions related to offers sending
         * and data usage.
         *
         * @returns {AxiosPromise}
         */
        Object.defineProperty(this, "getFullUserProfile", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.profileLoading = true;
                return ProfileAPI.getFullUserProfile()
                    .then((response) => {
                    this.profileLoading = false;
                    Object.assign(this.userProfile, response[0]);
                    Object.assign(this.userPermissions, response[1]);
                    return { profile: response[0], permissions: response[1] };
                })
                    .catch((errors) => {
                    this.profileLoading = false;
                    this.errors = errors;
                });
            }
        });
        /**
         * Loads user contact details only.
         * Without user permissions.
         *
         * @returns {AxiosPromise}
         */
        Object.defineProperty(this, "getProfileData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.profileLoading = true;
                return ProfileAPI.getUserProfileData()
                    .then((response) => {
                    this.profileLoading = false;
                    Object.assign(this.userProfile, response);
                })
                    .catch((error) => {
                    this.profileLoading = false;
                    this.errors = error;
                });
            }
        });
        /**
         * Loads user permissions related to his data usage and offers sending.
         *
         * If user activated this two features then he receive better recommendations
         * and also emails related to product offers
         *
         * @returns {AxiosPromise}
         */
        Object.defineProperty(this, "getPermissionsData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.profileLoading = true;
                return ProfileAPI.getUserPermissionsData()
                    .then((response) => {
                    this.profileLoading = false;
                    Object.assign(this.userPermissions, response);
                })
                    .catch((error) => {
                    this.profileLoading = false;
                    this.errors = error;
                });
            }
        });
        /**
         * Sends request to the server to receive list of dealers related to the current user.
         *
         * User sees different vehicles in his vehicles list depending on selected dealer.
         *
         * @returns {Promise<AxiosResponse<any>>}
         */
        Object.defineProperty(this, "getDealers", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.dealersLoading = true;
                return ProfileAPI.getDealers()
                    .then((response) => {
                    this.dealersLoading = false;
                    return this.dealers = response;
                })
                    .catch(() => {
                    this.dealersLoading = false;
                });
            }
        });
        /**
         * Sends request to the server to change user's main dealer.
         *
         * User sees different vehicles in his vehicles list depending on
         * selected dealer. Please not that user's vehicle list needs to be reloaded
         * after main dealer change.
         *
         * @param {string} dealerId - selected dealerId
         * @returns {Promise<AxiosResponse<any>>}
         */
        Object.defineProperty(this, "switchDealer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (dealerId) => {
                return ProfileAPI.setMainDealer(dealerId)
                    .then((response) => {
                    return this.dealers = response;
                });
            }
        });
        /**
         * Sends a request to the server to update user profile.
         *
         * @param data - user profile object
         * @returns {AxiosPromise}
         */
        Object.defineProperty(this, "updateUserProfileData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (data) => {
                return ProfileAPI.updateUserProfileData(data);
            }
        });
        Object.defineProperty(this, "removeDeliveryAddressData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (id) => {
                return ProfileAPI.removeDeliveryAddressData(id).then(() => this.getProfileData());
            }
        });
        Object.defineProperty(this, "updateUserProfile", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (data) => {
                return ProfileAPI.updateUserProfile(data);
            }
        });
        Object.defineProperty(this, "updateUserProfileBaseInfo", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (data) => {
                return ProfileAPI.updateUserProfileBaseInfo(data);
            }
        });
        Object.defineProperty(this, "updateUserProfileAdditionalAddress", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (data) => {
                return ProfileAPI.updateUserProfileAdditionalAddress(data);
            }
        });
        Object.defineProperty(this, "createUserProfile", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (data) => {
                return ProfileAPI.createUserProfile(data);
            }
        });
        Object.defineProperty(this, "setDeliveryAddressesProfile", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (selectedValue) => {
                return this.selectedDeiveryAddressValue = selectedValue;
            }
        });
        Object.defineProperty(this, "setDeliveryCheckoutAddressObj", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (selectedValue) => {
                return this.selectedDeliveryCheckoutAddressObj = selectedValue;
            }
        });
        Object.defineProperty(this, "createUserProfileWithoutPassword", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (data) => {
                return ProfileAPI.createUserProfileWithoutPassword(data).then((result) => {
                    return this.tempPassword = result.pw;
                });
            }
        });
        Object.defineProperty(this, "disableProfile", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (token) => {
                return ProfileAPI.disableProfile(token);
            }
        });
        Object.defineProperty(this, "createUpdateUserProfileWithHelpSaleAgent", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (data) => {
                return ProfileAPI.createUpdateUserProfileWithHelpSaleAgent(data);
            }
        });
        // The Konto menu only asks whether there is an order: the same Odoo leads /account/my-orders
        // lists (odoo-sync leads?wonStatus=pending, the count leaves out the unprocessed ones too).
        Object.defineProperty(this, "getRequestedCarsCount", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                return OdooSyncAPI.getRequestedCarsCount()
                    .then((total) => this.totalOrdered = Number(total) || 0)
                    .catch(() => {
                    ShowNotificationError();
                    return this.totalOrdered = 0;
                });
            }
        });
        Object.defineProperty(this, "getPendingRequestedCars", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                if (this.orderedCars.length !== 0) {
                    return;
                }
                this.isFetching = true;
                return OdooSyncAPI.getPendingRequestedCars().then((result) => {
                    this.isFetching = false;
                    return this.orderedCars = result.data;
                }).catch(() => {
                    this.isFetching = false;
                    this.orderedCars = [];
                    return ShowNotificationError();
                });
            }
        });
        Object.defineProperty(this, "getRequestedCars", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                if (this.requestedCars.length !== 0) {
                    return;
                }
                return OdooSyncAPI.getRequestedCars()
                    .then((cars) => {
                    return this.requestedCars = cars.data;
                }).catch(() => {
                    this.requestedCars = [];
                    return ShowNotificationError();
                });
            }
        });
        Object.defineProperty(this, "getWonCars", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                if (this.wonCars.length !== 0) {
                    return;
                }
                return OdooSyncAPI.getWonCars().then((cars) => {
                    return this.wonCars = cars.data;
                }).catch(() => {
                    this.wonCars = [];
                    return ShowNotificationError();
                });
            }
        });
        /**
         * In case when not-registered user decides to send OfferRequest but don't fill all contact form fields and tries to apply or when
         * closing request modal/canceling request modal - this method sends data from unfinished contact forms fields to server.
         * @param data
         */
        Object.defineProperty(this, "submitInvalidContactFormInformation", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (data) => {
                return UserActionsContactAPI.submitInvalidContactFormInformation(data);
            }
        });
        /**
         * Sends a request to the server to update permissions of the user.
         *
         * Permissions related to user's data usage and product offers sending by the email.
         * For more info please read getPermissionsData section above.
         * @param {IUserPermissions} data
         * @returns {AxiosPromise}
         */
        Object.defineProperty(this, "updateUserPermissions", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (data, permissionText) => {
                this.permissionsSubmitting = true;
                return ProfileAPI.updateUserPermissionData(data, permissionText)
                    .then(() => {
                    this.permissionsSubmitting = false;
                })
                    .catch(() => {
                    this.permissionsSubmitting = false;
                });
            }
        });
        /**
         * Sends a request to the server to get list of API partners for data import.
         *
         * @returns {AxiosPromise}
         */
        Object.defineProperty(this, "getApiPartners", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.isFetching = true;
                return ProfileAPI.getApiPartners()
                    .then((apiPartners) => {
                    this.apiPartners = apiPartners;
                    this.isFetching = false;
                    this.hasError = false;
                    return apiPartners;
                })
                    .catch(() => {
                    this.isFetching = false;
                    this.hasError = true;
                    return [];
                });
            }
        });
        Object.defineProperty(this, "getPlatforms", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.isFetching = true;
                return ProfileAPI.getPlatforms()
                    .then((platforms) => {
                    this.platforms = platforms;
                    this.isFetching = false;
                    this.hasError = false;
                    return platforms;
                })
                    .catch(() => {
                    this.isFetching = false;
                    this.hasError = true;
                    return [];
                });
            }
        });
        Object.defineProperty(this, "addPlatform", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (config) => {
                this.isFetching = true;
                return ProfileAPI.addPlatform(config)
                    .then(() => {
                    this.isFetching = false;
                    this.hasError = false;
                })
                    .catch(() => {
                    this.isFetching = false;
                    this.hasError = true;
                });
            }
        });
        Object.defineProperty(this, "changePlatform", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (configId, config) => {
                this.isFetching = true;
                return ProfileAPI.changePlatform(configId, config)
                    .then(() => {
                    this.isFetching = false;
                    this.hasError = false;
                })
                    .catch(() => {
                    this.isFetching = false;
                    this.hasError = true;
                });
            }
        });
        Object.defineProperty(this, "getDealerGTC", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (lng) => {
                this.isFetching = true;
                return ProfileAPI.getDealerGTC(lng)
                    .then((response) => {
                    this.isFetching = false;
                    return response;
                })
                    .catch(() => {
                    this.isFetching = false;
                    this.hasError = true;
                });
            }
        });
        Object.defineProperty(this, "getDealerProfile", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.isFetching = true;
                return ProfileAPI.getDealerProfile()
                    .then((response) => {
                    this.dealerProfile = response;
                    this.areTermsFilled = this.dealerProfile.termCond !== null;
                    this.isFetching = false;
                    return response;
                })
                    .catch(() => {
                    this.isFetching = false;
                    this.hasError = true;
                });
            }
        });
        Object.defineProperty(this, "getDealerProfileCheckout", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (id) => {
                this.isFetching = true;
                return ProfileAPI.getDealerProfileCheckout(id)
                    .then((response) => {
                    this.isFetching = false;
                    return response;
                })
                    .catch(() => {
                    this.isFetching = false;
                    this.hasError = true;
                    return {};
                });
            }
        });
        Object.defineProperty(this, "getDealerProfileEdit", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => new Promise((resolve, reject) => {
                const isPresent = (obj) => Object.values(obj).length !== 0;
                if (!this.isFetching) {
                    if (isPresent(this.dealerProfileEdit)) {
                        return resolve(this.dealerProfileEdit);
                    }
                    if (isPresent(this.dealerProfile)) {
                        this.dealerProfileEdit = transform(this.dealerProfile);
                        return resolve(this.dealerProfileEdit);
                    }
                    this.getDealerProfile().catch((error) => {
                        reject(error);
                    });
                }
                when(() => !this.isFetching && isPresent(this.dealerProfile), () => {
                    this.dealerProfileEdit = transform(this.dealerProfile);
                    resolve(this.dealerProfileEdit);
                });
            })
        });
        Object.defineProperty(this, "patchDealerProfileEdit", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (partial) => {
                const dealerProfileEdit = toJS(this.dealerProfileEdit);
                Object.entries(partial).forEach(([key, newValue]) => {
                    const oldValue = dealerProfileEdit[key];
                    dealerProfileEdit[key] = oldValue instanceof Object && newValue instanceof Object ? { ...oldValue, ...newValue } : newValue;
                });
                this.dealerProfileEdit = dealerProfileEdit;
            }
        });
        Object.defineProperty(this, "saveDealerProfile", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (password, permissionText) => {
                this.isSaving = true;
                return ProfileAPI.createDealerProfile({ password, ...toJS(this.dealerProfileEdit) }, permissionText).finally(() => {
                    this.isSaving = false;
                });
            }
        });
        Object.defineProperty(this, "saveDealerProfileEdit", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (permissionText) => {
                this.isSaving = true;
                return ProfileAPI.putDealerProfile(this.dealerProfileEdit, permissionText).finally(() => {
                    this.isSaving = false;
                });
            }
        });
        Object.defineProperty(this, "clearDealerProfile", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.dealerProfile = {};
            }
        });
        Object.defineProperty(this, "clearDealerProfileEdit", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.dealerProfileEdit = {};
            }
        });
        Object.defineProperty(this, "clearUserProfileState", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.userProfile = DEFAULT_USER_PROFILE;
            }
        });
        Object.defineProperty(this, "clearPlatforms", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.platforms = [];
            }
        });
        Object.defineProperty(this, "clearTempPassword", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                this.tempPassword = null;
            }
        });
        makeObservable(this);
    }
    get isProfileLoaded() {
        return !this.profileLoading && !this.errors.length && !!Object.keys(this.userProfile).length;
    }
    get isProfileEmpty() {
        return !this.profileLoading && !!Object.keys(this.userProfile).length;
    }
    get isDealersArray() {
        return this.hasDealers && Array.isArray(toJS(this.dealers));
    }
    get activeDealerId() {
        if (this.dealers) {
            const activeDealer = this.dealers.find((dealer) => dealer.isActive);
            return activeDealer.dealer.id;
        }
    }
    get hasDealers() {
        const dealersLength = this.dealers.length;
        return !!dealersLength;
    }
    get hasDealersAddress() {
        if (this.dealers) {
            const activeDealer = this.dealers.find((dealer) => dealer.isActive);
            return !!activeDealer.dealer.userAddress;
        }
    }
    get activeDealerCompany() {
        if (this.hasDealers && this.hasDealersAddress) {
            const activeDealer = this.dealers.find((dealer) => dealer.isActive);
            return activeDealer.dealer.userAddress.company;
        }
        return DEFAULT_EMPTY_VALUE;
    }
    get hasPrivateData() {
        const { privateAddress } = this.userProfile;
        return !!privateAddress;
    }
    get hasProfileCompleted() {
        const { privateAddress } = this.userProfile;
        if (!privateAddress) {
            return false;
        }
        return [
            privateAddress.firstName,
            privateAddress.lastName,
            privateAddress.gender,
            privateAddress.city,
            privateAddress.number,
            privateAddress.zip,
            privateAddress.phoneMain
        ].every(value => value !== null && value !== undefined);
    }
    get hasDeliveryData() {
        const { deliveryAddress } = this.userProfile;
        return !!deliveryAddress;
    }
    get profileName() {
        const { privateAddress } = this.userProfile;
        let profileName = '';
        let formattedProfileName = '';
        if (this.hasPrivateData && (privateAddress.firstName || privateAddress.lastName)) {
            if (privateAddress.firstName) {
                profileName = profileName + `${privateAddress.firstName} `; // eslint-disable-line
                formattedProfileName = formattedProfileName + `${privateAddress.firstName.charAt(0).toUpperCase() + privateAddress.firstName.slice(1)} `; // eslint-disable-line
            }
            if (privateAddress.lastName) {
                profileName = profileName + `${privateAddress.lastName}`; // eslint-disable-line
                formattedProfileName = formattedProfileName + `${privateAddress.lastName.charAt(0).toUpperCase() + privateAddress.lastName.slice(1)} `; // eslint-disable-line
            }
            const displayName = localStorage.getItem('auto_jwt_displayName');
            if (displayName === ' ') {
                localStorage.setItem(LOCAL_STORAGE_KEY.JWT_DISPLAY_NAME_KEY, formattedProfileName);
            }
            return profileName;
        }
        return DEFAULT_EMPTY_VALUE;
    }
    get profileGender() {
        const { privateAddress } = this.userProfile;
        if (this.hasPrivateData && (privateAddress.gender || privateAddress.gender === 0)) {
            return GENDERS[privateAddress.gender].label;
        }
        return null;
    }
    get profileTitle() {
        const { privateAddress } = this.userProfile;
        if (this.hasPrivateData && (privateAddress.title || privateAddress.title === 0)) {
            return TITLES[privateAddress.title].label;
        }
        return null;
    }
    get profileEmail() {
        return this.userProfile.email ? this.userProfile.email : '';
    }
    get hasMainData() {
        const { mainAddress } = this.dealerProfile;
        return !!mainAddress;
    }
    get profileDealerName() {
        const { mainAddress } = this.dealerProfile;
        let string = '';
        if (this.hasMainData && (mainAddress.firstName || mainAddress.lastName)) {
            if (mainAddress.firstName) {
                string = string + `${mainAddress.firstName} `; // eslint-disable-line
            }
            if (mainAddress.lastName) {
                string = string + `${mainAddress.lastName}`; // eslint-disable-line
            }
            return string;
        }
        return DEFAULT_EMPTY_VALUE;
    }
    get profilePhone() {
        const { privateAddress } = this.userProfile;
        if (this.hasPrivateData && privateAddress.phoneMain) {
            return privateAddress.phoneMain;
        }
        return DEFAULT_EMPTY_VALUE;
    }
    get profileCompany() {
        const { privateAddress } = this.userProfile;
        if (this.hasPrivateData && privateAddress.company) {
            return privateAddress.company;
        }
        return DEFAULT_EMPTY_VALUE;
    }
    get firstAddressLine() {
        const { privateAddress } = this.userProfile;
        let string = '';
        if (this.hasPrivateData) {
            if (privateAddress.street) {
                string = string + `${privateAddress.street} `; // eslint-disable-line
            }
            if (privateAddress.number) {
                string = string + privateAddress.number;
            }
            return string;
        }
        return DEFAULT_EMPTY_VALUE;
    }
    get secondAddressLine() {
        const { privateAddress } = this.userProfile;
        let string = '';
        if (this.hasPrivateData) {
            if (privateAddress.country) {
                string = string + `${privateAddress.country.alpha2} `; // eslint-disable-line
            }
            if (privateAddress.zip) {
                string = string + `- ${privateAddress.zip} `; // eslint-disable-line
            }
            if (privateAddress.city) {
                string = string + privateAddress.city;
            }
            if (this.firstAddressLine !== 'empty') {
                return string;
            }
            // we return false here just because we do not want
            // to show anything if there is no address line
            return null;
        }
    }
    get isImprintApproved() {
        const status = _get(this, 'dealerProfile.imprint.status');
        return status === DealerStatus.APPROVED || status === DealerStatus.TEMPORARY;
    }
    get isTermCondApproved() {
        const status = _get(this, 'dealerProfile.termCond.status');
        return status === DealerStatus.APPROVED || status === DealerStatus.TEMPORARY;
    }
}
__decorate([
    observable,
    __metadata("design:type", Object)
], ProfileStore.prototype, "userProfile", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], ProfileStore.prototype, "userPermissions", void 0);
__decorate([
    observable,
    __metadata("design:type", Boolean)
], ProfileStore.prototype, "permissionsSubmitting", void 0);
__decorate([
    observable,
    __metadata("design:type", Boolean)
], ProfileStore.prototype, "dealersLoading", void 0);
__decorate([
    observable,
    __metadata("design:type", Boolean)
], ProfileStore.prototype, "profileLoading", void 0);
__decorate([
    observable,
    __metadata("design:type", Array)
], ProfileStore.prototype, "errors", void 0);
__decorate([
    observable,
    __metadata("design:type", Boolean)
], ProfileStore.prototype, "isFormUpdated", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], ProfileStore.prototype, "dealers", void 0);
__decorate([
    observable,
    __metadata("design:type", Boolean)
], ProfileStore.prototype, "isProfileFilled", void 0);
__decorate([
    observable,
    __metadata("design:type", Boolean)
], ProfileStore.prototype, "areTermsFilled", void 0);
__decorate([
    observable,
    __metadata("design:type", Number)
], ProfileStore.prototype, "totalOrdered", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], ProfileStore.prototype, "orderedCars", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], ProfileStore.prototype, "requestedCars", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], ProfileStore.prototype, "wonCars", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], ProfileStore.prototype, "isFetching", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], ProfileStore.prototype, "isSaving", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], ProfileStore.prototype, "hasError", void 0);
__decorate([
    observable,
    __metadata("design:type", String)
], ProfileStore.prototype, "selectedDeiveryAddressValue", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], ProfileStore.prototype, "selectedDeliveryCheckoutAddressObj", void 0);
__decorate([
    observable,
    __metadata("design:type", Array)
], ProfileStore.prototype, "apiPartners", void 0);
__decorate([
    observable,
    __metadata("design:type", Array)
], ProfileStore.prototype, "platforms", void 0);
__decorate([
    observable,
    __metadata("design:type", typeof (_a = typeof IDealerProfile !== "undefined" && IDealerProfile) === "function" ? _a : Object)
], ProfileStore.prototype, "dealerProfile", void 0);
__decorate([
    observable,
    __metadata("design:type", typeof (_b = typeof IDealerProfileEditType !== "undefined" && IDealerProfileEditType) === "function" ? _b : Object)
], ProfileStore.prototype, "dealerProfileEdit", void 0);
__decorate([
    observable,
    __metadata("design:type", String)
], ProfileStore.prototype, "tempPassword", void 0);
__decorate([
    computed,
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [])
], ProfileStore.prototype, "isProfileLoaded", null);
__decorate([
    computed,
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [])
], ProfileStore.prototype, "isProfileEmpty", null);
__decorate([
    computed,
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [])
], ProfileStore.prototype, "isDealersArray", null);
__decorate([
    computed,
    __metadata("design:type", String),
    __metadata("design:paramtypes", [])
], ProfileStore.prototype, "activeDealerId", null);
__decorate([
    computed,
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [])
], ProfileStore.prototype, "hasDealers", null);
__decorate([
    computed,
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [])
], ProfileStore.prototype, "hasDealersAddress", null);
__decorate([
    computed,
    __metadata("design:type", String),
    __metadata("design:paramtypes", [])
], ProfileStore.prototype, "activeDealerCompany", null);
__decorate([
    computed,
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [])
], ProfileStore.prototype, "hasPrivateData", null);
__decorate([
    computed,
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [])
], ProfileStore.prototype, "hasProfileCompleted", null);
__decorate([
    computed,
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [])
], ProfileStore.prototype, "hasDeliveryData", null);
__decorate([
    computed,
    __metadata("design:type", String),
    __metadata("design:paramtypes", [])
], ProfileStore.prototype, "profileName", null);
__decorate([
    computed,
    __metadata("design:type", String),
    __metadata("design:paramtypes", [])
], ProfileStore.prototype, "profileGender", null);
__decorate([
    computed,
    __metadata("design:type", String),
    __metadata("design:paramtypes", [])
], ProfileStore.prototype, "profileTitle", null);
__decorate([
    computed,
    __metadata("design:type", String),
    __metadata("design:paramtypes", [])
], ProfileStore.prototype, "profileEmail", null);
__decorate([
    computed,
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [])
], ProfileStore.prototype, "hasMainData", null);
__decorate([
    computed,
    __metadata("design:type", String),
    __metadata("design:paramtypes", [])
], ProfileStore.prototype, "profileDealerName", null);
__decorate([
    computed,
    __metadata("design:type", String),
    __metadata("design:paramtypes", [])
], ProfileStore.prototype, "profilePhone", null);
__decorate([
    computed,
    __metadata("design:type", String),
    __metadata("design:paramtypes", [])
], ProfileStore.prototype, "profileCompany", null);
__decorate([
    computed,
    __metadata("design:type", String),
    __metadata("design:paramtypes", [])
], ProfileStore.prototype, "firstAddressLine", null);
__decorate([
    computed,
    __metadata("design:type", String),
    __metadata("design:paramtypes", [])
], ProfileStore.prototype, "secondAddressLine", null);
__decorate([
    computed,
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [])
], ProfileStore.prototype, "isImprintApproved", null);
__decorate([
    computed,
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [])
], ProfileStore.prototype, "isTermCondApproved", null);
__decorate([
    action,
    __metadata("design:type", Object)
], ProfileStore.prototype, "clearDealers", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ProfileStore.prototype, "getFullUserProfile", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ProfileStore.prototype, "getProfileData", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ProfileStore.prototype, "getPermissionsData", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ProfileStore.prototype, "getDealers", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ProfileStore.prototype, "switchDealer", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ProfileStore.prototype, "updateUserProfileData", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ProfileStore.prototype, "removeDeliveryAddressData", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ProfileStore.prototype, "updateUserProfile", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ProfileStore.prototype, "updateUserProfileBaseInfo", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ProfileStore.prototype, "updateUserProfileAdditionalAddress", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ProfileStore.prototype, "createUserProfile", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ProfileStore.prototype, "setDeliveryAddressesProfile", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ProfileStore.prototype, "setDeliveryCheckoutAddressObj", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ProfileStore.prototype, "createUserProfileWithoutPassword", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ProfileStore.prototype, "disableProfile", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ProfileStore.prototype, "createUpdateUserProfileWithHelpSaleAgent", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ProfileStore.prototype, "getRequestedCarsCount", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ProfileStore.prototype, "getPendingRequestedCars", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ProfileStore.prototype, "getRequestedCars", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ProfileStore.prototype, "getWonCars", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ProfileStore.prototype, "submitInvalidContactFormInformation", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ProfileStore.prototype, "updateUserPermissions", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ProfileStore.prototype, "getApiPartners", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ProfileStore.prototype, "getPlatforms", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ProfileStore.prototype, "addPlatform", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ProfileStore.prototype, "changePlatform", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ProfileStore.prototype, "getDealerGTC", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ProfileStore.prototype, "getDealerProfile", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ProfileStore.prototype, "getDealerProfileCheckout", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ProfileStore.prototype, "getDealerProfileEdit", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ProfileStore.prototype, "patchDealerProfileEdit", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ProfileStore.prototype, "saveDealerProfile", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ProfileStore.prototype, "saveDealerProfileEdit", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ProfileStore.prototype, "clearDealerProfile", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ProfileStore.prototype, "clearDealerProfileEdit", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ProfileStore.prototype, "clearUserProfileState", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ProfileStore.prototype, "clearPlatforms", void 0);
__decorate([
    action,
    __metadata("design:type", Object)
], ProfileStore.prototype, "clearTempPassword", void 0);
export default new ProfileStore();
